import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useProposalList } from '@/hooks/useProposal';
import { ProposalStatusBadge } from '@/components/proposal/ProposalStatusBadge';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { PROPOSAL_PERMISSIONS, hasPermission } from '@/lib/proposalPermissions';
import type { ApiComponents } from '@jinzhuan/api-client';

type ProposalStatus = ApiComponents['schemas']['ProposalDto']['status'];

const STATUS_OPTIONS: Array<{ value: ProposalStatus | ''; label: string }> = [
  { value: '', label: '全部' },
  { value: 'draft', label: '草稿' },
  { value: 'in_review', label: '审核中' },
  { value: 'approved', label: '已批准' },
  { value: 'rejected', label: '已拒绝' },
  { value: 'locked', label: '已锁定' },
  { value: 'cancelled', label: '已取消' },
];

export default function ProposalListPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { proposals, loading, error, fetchProposals } = useProposalList();

  const [filterStatus, setFilterStatus] = useState<ProposalStatus | ''>('');
  const [filterCustomerId, setFilterCustomerId] = useState('');

  const canRead = hasPermission(permissions, PROPOSAL_PERMISSIONS.PROPOSAL_READ);
  const canCreate = hasPermission(permissions, PROPOSAL_PERMISSIONS.PROPOSAL_CREATE);

  useEffect(() => {
    if (!canRead) return;
    void fetchProposals({
      status: filterStatus || undefined,
      customerId: filterCustomerId || undefined,
    });
  }, [filterStatus, filterCustomerId, fetchProposals, canRead]);

  if (!canRead) {
    return <AccessDenied resource="方案列表" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">方案管理</h1>
        {canCreate && (
          <button
            onClick={() => navigate('/proposals/create')}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            新建方案
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">状态</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as ProposalStatus | '')}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">客户 ID</label>
          <input
            type="text"
            value={filterCustomerId}
            onChange={(e) => setFilterCustomerId(e.target.value)}
            placeholder="输入客户 ID"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
          加载失败：{error.message}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">加载中...</div>
      ) : (
        <div className="rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium">标题</th>
                <th className="px-4 py-3 text-left font-medium">客户 ID</th>
                <th className="px-4 py-3 text-left font-medium">状态</th>
                <th className="px-4 py-3 text-left font-medium">创建时间</th>
                <th className="px-4 py-3 text-left font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {proposals.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    暂无方案记录
                  </td>
                </tr>
              ) : (
                proposals.map((proposal) => (
                  <tr key={proposal.id} className="border-b hover:bg-muted/25">
                    <td className="px-4 py-3 font-medium">{proposal.title}</td>
                    <td className="px-4 py-3 font-mono text-xs">{proposal.customerId}</td>
                    <td className="px-4 py-3">
                      <ProposalStatusBadge status={proposal.status} />
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(proposal.createdAt).toLocaleString('zh-CN', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/proposals/${proposal.id}`)}
                        className="text-xs text-primary hover:underline"
                      >
                        详情
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
